/**
 * Keeping chosen work together (SRS C4).
 *
 * A group lives for as long as the board is open and is never written to the board
 * itself. That is deliberate: grouping is a way of working rather than a property of
 * the drawing, so a lecture reopened next week gives back exactly the strokes that
 * were drawn, and nothing about the way lectures are stored had to change to make
 * grouping work.
 */

import type { Stroke } from '@opal/shared';

import type { SelectableInk, SelectionActions } from './select-session.js';

/** What grouping needs of the selection it works on. */
export interface Grouping {
  readonly ink: SelectableInk;
  selected: Stroke[];
  /** Work that has been grouped, by stroke id. */
  groups: Set<string>[];
  /** Says that what is chosen, or how it is held together, has changed. */
  announce(): void;
  /** Letting go of the selection: the tool drops any drag in progress with it. */
  onClear(): void;
}

/** True when any of the chosen work belongs to a group. */
export function isGrouped(state: Grouping): boolean {
  const chosen = new Set(state.selected.map((stroke) => stroke.id));
  return state.groups.some((group) => [...group].some((id) => chosen.has(id)));
}

/**
 * The chosen work, plus everything grouped with it.
 *
 * Touching one part of a grouped diagram takes the whole diagram, which is the whole
 * point of having grouped it.
 */
export function withGroups(state: Grouping, strokes: readonly Stroke[]): Stroke[] {
  const chosen = new Map(strokes.map((stroke) => [stroke.id, stroke]));
  for (const group of state.groups) {
    if (![...group].some((id) => chosen.has(id))) continue;
    for (const id of group) {
      if (chosen.has(id)) continue;
      const stroke = state.ink.find(id);
      // A member somebody has since erased is simply not there to take.
      if (stroke) chosen.set(id, stroke);
    }
  }
  return [...chosen.values()];
}

/** Forgets work that has gone, and any group left with nothing to hold together. */
export function forgetIds(state: Grouping, ids: readonly string[]): void {
  const gone = new Set(ids);
  state.groups = state.groups
    .map((group) => new Set([...group].filter((id) => !gone.has(id))))
    .filter((group) => group.size > 1);
}

/** SRS C4 — grouping, ungrouping, and letting go of the selection. */
export function groupActions(
  state: Grouping,
): Pick<SelectionActions, 'group' | 'ungroup' | 'clear'> {
  return {
    group: () => {
      if (state.selected.length < 2) return;
      const ids = new Set(state.selected.map((stroke) => stroke.id));
      // Grouping work that is already part of a group joins the two together, rather
      // than leaving one piece belonging to both.
      state.groups = state.groups.filter((group) => {
        if (![...group].some((id) => ids.has(id))) return true;
        for (const id of group) ids.add(id);
        return false;
      });
      state.groups.push(ids);
      state.selected = withGroups(state, state.selected);
      state.announce();
    },

    ungroup: () => {
      if (!isGrouped(state)) return;
      const chosen = new Set(state.selected.map((stroke) => stroke.id));
      state.groups = state.groups.filter((group) => ![...group].some((id) => chosen.has(id)));
      state.announce();
    },

    clear: () => {
      const wasEmpty = state.selected.length === 0;
      state.selected = [];
      state.onClear();
      if (!wasEmpty) state.announce();
    },
  };
}
